// Floor-plan upload help follows the portal's DXF-only upload and the real
// Setup-pipeline state machine. It intentionally states no file-size limit
// while the portal label and API default disagree.
import type { HelpArticle } from "../../types";
import { PROJECT_HELP_PROJECT } from "../../../project/projectPreviewFixtures";

export const floorPlansArticle: HelpArticle = {
  id: "floor-plans",
  title: "Uploading floor plans",
  description:
    "Upload or replace the project's DXF base plan, read the Setup pipeline states, and know exactly when the viewer becomes available.",
  tags: ["DXF", "floor plan", "upload", "Setup pipeline", "object identification"],
  lastUpdated: "July 19, 2026",
  published: true,
  blocks: [
    {
      kind: "lead",
      text: "Uploading a floor plan starts work immediately. Firmark accepts the project's DXF base plan, starts the Setup pipeline, and keeps the viewer closed while that run identifies the objects you will review. The filename records what was uploaded; the pipeline state tells you whether the plan is actually ready.",
    },

    { kind: "heading", level: 2, text: "Before the upload", id: "before-upload" },
    { kind: "where", path: ["Design projects", PROJECT_HELP_PROJECT.name, "Floor plan"] },
    {
      kind: "paragraph",
      text: "The Floor plan section appears only after Site conditions, Hazard data, Soil, Foundation, and Engineering profile are confirmed. That ordering binds the plan run to a reviewed project basis. If the section is absent, return to the first setup row still marked **Review & confirm**.",
    },
    {
      kind: "callout",
      variant: "info",
      title: "DXF is the accepted base-plan format",
      body: "The file picker accepts `.dxf`. Export the base plan to DXF before this step; do not rely on a renamed DWG or PDF extension.",
    },
    {
      kind: "shot",
      shotId: "floor-plan-upload",
      caption: "The DXF handoff explains the side effect before selection: uploading starts Setup object identification.",
    },

    { kind: "heading", level: 2, text: "Upload and run Setup", id: "upload" },
    {
      kind: "steps",
      steps: [
        {
          title: "Choose the DXF",
          body: "Drag the base plan onto the upload zone or click the zone and select it. The portal posts that file as the project's floor-plan upload.",
        },
        {
          title: "Let the run start",
          body: "A successful upload records the filename and starts the project pipeline unless a run is already active or parked at a review gate. On the first upload, if starting the run fails, the portal rolls back its recorded upload state so the page does not claim a usable plan exists.",
        },
        {
          title: "Watch Preparing…",
          body: "While the latest run is active, the viewer action is disabled and shows the elapsed preparation time. The Floor plan row states that Setup is identifying objects.",
        },
        {
          title: "Open the identified plan",
          body: "When the run's `setup_bundle` stage succeeds and the project confirmations still hold, the row reads **setup complete — open the viewer to review** and the viewer action becomes available.",
        },
      ],
    },
    {
      kind: "shot",
      shotId: "floor-plan-processing",
      caption: "The latest run is active: the uploaded filename is retained while the viewer remains in Preparing state.",
    },
    {
      kind: "paragraph",
      text: "A pipeline failure is a visible terminal state, not a silent timeout. The Floor plan row says Setup failed, and the viewer control becomes **Retry setup** with the run error available as its explanation. Retrying starts the pipeline again against the recorded plan.",
    },

    { kind: "heading", level: 2, text: "Ready means a produced setup bundle", id: "ready" },
    {
      kind: "paragraph",
      text: "Firmark derives readiness from the latest pipeline run. The normal evidence is a succeeded `setup_bundle` stage, which stays usable while later stages run. A latest run reported as completed also counts as ready for compatibility. A recorded filename by itself does not.",
    },
    {
      kind: "shot",
      shotId: "floor-plan-ready",
      caption: "The Setup bundle succeeded: the project now offers the real identified plan to the viewer.",
    },

    { kind: "heading", level: 2, text: "Replacing the plan", id: "replace" },
    {
      kind: "paragraph",
      text: "After an upload, the drop zone becomes a compact filename row with **Replace file**. A replacement is intentional: the portal preserves the prior recorded plan if the replacement upload itself fails. When the replacement succeeds, it records the new file and starts Setup again when the pipeline is eligible to run.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Replacement creates new review work",
      body: "The viewer is reviewing objects identified from a particular DXF run. Replace the plan only when you intend to generate a new Setup result; do not treat Replace file as a filename correction.",
    },

    { kind: "related", topics: ["project-setup", "hazard-reports", "viewer-review"] },
  ],
};
